import { HttpClient } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { environment } from "@uw-env/environment";
import { Observable, map } from 'rxjs';
import { CampaignEmailMgmtResponse, CreateUpdateCampaignEmailRequest, CreateUpdateEmailRequest, ProfileEmailMgmtResponse, ScheduleCampaignEmailRequest, ScheduledCampaignEmailResponse, SendAdminEmailRequest } from "../api/models";
import { UWEmailMgmtResponse } from "../api/models/uw-email-mgmt-response";
import { UwEmailDetailResponse } from "../api/models/uw-email-detail-response";
import { BaseApiService } from "./baseapi.service";
import { DownloadEmailReportRequest } from "@uw/core/api/models/download-email-report-request";

@Injectable({
  providedIn: "root"
})
export class EmailMgmtService extends BaseApiService {
  constructor(
    private readonly _client: HttpClient
  ) {
    super();
  }

  downloadEmailReport(campaignId: string, request: DownloadEmailReportRequest){
    const url = `${environment.apiURL}/administration/emailMgmt/schedule/campaignEmailRecipient/${campaignId}`;
    return this.httpAuthPostDownload(url, request);
  }

  getAllEmailsByCampaignId(campaignId: any): Observable<CampaignEmailMgmtResponse[]> {
    const url = `${environment.apiURL}/administration/emailMgmt/campaign/${campaignId}`;
    return this._client.get<{ data: CampaignEmailMgmtResponse[] }>(url).pipe(
      map(res => res.data)
    )
  }

  getAllScheduleEmailsByCampaignId(campaignId: any): Observable<ScheduledCampaignEmailResponse[]> {
    const url = `${environment.apiURL}/administration/emailMgmt/schedule/view/${campaignId}`;
    return this._client.get<{ data: ScheduledCampaignEmailResponse[] }>(url).pipe(
      map(res => res.data)
    )
  }

  getEmailsByUw(): Observable<UWEmailMgmtResponse[]> {
    const url = `${environment.apiURL}/administration/EmailMgmt/unitedway/emails`;
    return this._client.get<{ data: UWEmailMgmtResponse[] }>(url).pipe(
      map(response => response.data)
    )
  }

  getByCampaignEmailId(campaignEmailId: string): Observable<CampaignEmailMgmtResponse> {
    const url = `${environment.apiURL}/administration/emailMgmt/detail/${campaignEmailId}`;
    return this._client.get<{ data: CampaignEmailMgmtResponse }>(url).pipe(
      map(res => res.data)
    )
  }

  sendEmail(sendAdminEmailRequest: SendAdminEmailRequest) {
    const url = `${environment.apiURL}/administration/emailMgmt/send`;
    return this._client.post<void>(url, sendAdminEmailRequest);
  }

  get(profileId: string): Observable<ProfileEmailMgmtResponse[]> {
    const url = `${environment.apiURL}/administration/emailMgmt/profile/${profileId}`;
    return this._client.get<{ data: any[] }>(url).pipe(
      map(response => response.data)
    )
  }

  getById(profileEmailId: string): Observable<ProfileEmailMgmtResponse> {
    const url = `${environment.apiURL}/administration/emailMgmt/email-detail/${profileEmailId}`;
    return this._client.get<{ data: ProfileEmailMgmtResponse }>(url).pipe(
      map(response => response.data)
    )
  }

  getUwEmailDetailById(emailTypeId: string): Observable<UwEmailDetailResponse> {
    const url = `${environment.apiURL}/administration/emailMgmt/unitedway/emaildetail/${emailTypeId}`;
    return this._client.get<{ data: UwEmailDetailResponse }>(url).pipe(
      map(response => response.data)
    )
  }

  getEmailTypes(): Observable<any[]> {
    const url = `${environment.apiURL}/administration/emailMgmt/EmailTypes`;
    return this._client.get<{ data: any[] }>(url).pipe(
      map(response => response.data)
    )
  }

  getEmployeeEmailTypeById(employeeEmailTypeId: any): Observable<any> {
    const url = `${environment.apiURL}/administration/emailMgmt/employee-email-type/${employeeEmailTypeId}`;
    return this._client.get<{ data: any }>(url).pipe(
      map(response => response.data)
    )
  }

  getLanguages(profileId: any): Observable<any> {
    const url = `${environment.apiURL}/administration/emailMgmt/profile/${profileId}/languages`;
    return this._client.get<{ data: any }>(url).pipe(
      map(response => response.data)
    )
  }

  update(request: CreateUpdateEmailRequest): Observable<ProfileEmailMgmtResponse> {
    const url = `${environment.apiURL}/administration/emailMgmt/update`;
    return this._client.post<{ data: ProfileEmailMgmtResponse }>(url, request).pipe(
      map(response => response.data)
    )
  }

  updateUwEmail(emailTypeId: string, request: CreateUpdateEmailRequest): Observable<UwEmailDetailResponse> {
    const url = `${environment.apiURL}/administration/emailMgmt/unitedway/emaildetail/${emailTypeId}`;
    return this._client.put<{ data: UwEmailDetailResponse }>(url, request).pipe(
      map(response => response.data)
    )
  }

  updateCampaignEmail(request: CreateUpdateCampaignEmailRequest): Observable<CampaignEmailMgmtResponse> {
    request.body = request.body ?? '';
    const url = `${environment.apiURL}/administration/emailMgmt/update/campaignEmail`;
    return this._client.post<{ data: CampaignEmailMgmtResponse }>(url, request).pipe(
      map(response => response.data)
    )
  }

  scheduleCampaignEmail(request: ScheduleCampaignEmailRequest): Observable<CampaignEmailMgmtResponse> {
    const url = `${environment.apiURL}/administration/emailMgmt/schedule/campaignEmail`;
    return this._client.post<{ data: CampaignEmailMgmtResponse }>(url, request).pipe(
      map(response => response.data)
    )
  }


  createCampaignEmail(request: CreateUpdateCampaignEmailRequest): Observable<CampaignEmailMgmtResponse | null> {
    request.body = request.body ?? '';
    const url = `${environment.apiURL}/administration/emailMgmt/create/campaignEmail`;
    return this._client.post<{ data: CampaignEmailMgmtResponse[] }>(url, request).pipe(
      map(response => response.data && response.data.length > 0 && response.data[0] || null)
    )
  }

  create(request: CreateUpdateEmailRequest): Observable<ProfileEmailMgmtResponse> {
    const url = `${environment.apiURL}/administration/emailMgmt/create`;
    return this._client.post<{ data: ProfileEmailMgmtResponse }>(url, request).pipe(
      map(response => response.data)
    )
  }

  removeEmail(request: ScheduledCampaignEmailResponse): Observable<ProfileEmailMgmtResponse> {
    const url = `${environment.apiURL}/administration/emailMgmt/schedule/delete/${request.scheduledCampaignEmailId}`;
    return this._client.delete<{ data: ProfileEmailMgmtResponse }>(url).pipe(
      map(response => response.data)
    )
  }

}